/*
 * custom-storefront.js
 *
 * Storefront-side runtime helpers that aren't easily handled by an app
 * block — currently the $0-price handling: on the PDP a $0 price is
 * swapped for a "Quote on request" badge.
 *
 * Cards and predictive search no longer rely on this file for $0: since
 * 2026-09-17 `snippets/price.liquid` renders the literal text "RFQ" when
 * called with `zero_as_rfq: true` (card-product.liquid + predictive-search
 * pass it). "RFQ" has no digits, so `isZeroPrice` reads false and the card
 * paths below are effectively no-ops; they are kept as a safety net for any
 * card rendered without that flag.
 *
 * Why this runs in JS instead of Liquid:
 *   - PDP price re-renders on variant change. Dawn's `product-info.js`
 *     calls `destination.innerHTML = source.innerHTML` for the price
 *     element, so any Liquid-side conditional needs to live inside the
 *     re-fetched section markup — which means editing Dawn's `price.liquid`,
 *     a snippet used across cards, PDP, cart, and search. One edit there
 *     impacts a lot of surface area and is fragile across Dawn updates.
 *   - Doing it as a small JS module that listens to Dawn's pub-sub event
 *     keeps the customization entirely inside our own `custom-*` files;
 *     the only Dawn-file edit is the single `<script>` tag in
 *     `layout/theme.liquid` that loads this file.
 *
 * Dawn-file edits this customization requires (re-apply on Dawn update):
 *   1. `layout/theme.liquid`         — load `custom-storefront.css` + this file.
 *   2. `snippets/card-product.liquid` — one-line `{% render 'custom-card-sku' %}`
 *                                       after the `show_vendor` block (line ~167).
 *
 * Everything else lives in `custom-*` files Dawn never ships and never
 * overwrites.
 */
(function () {
  if (window.__customStorefrontLoaded) return;
  window.__customStorefrontLoaded = true;

  var QUOTE_TEXT = 'Quote on request';

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  onReady(function () {
    processAllCards();
    processPdp();

    // PDP price re-renders on variant change. Dawn publishes `variant-change`
    // after `updateSourceFromDestination('price', ...)` rewrites the price
    // element's innerHTML — so by the time this handler runs, the new
    // variant's price text is already in the DOM and we just re-evaluate.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, function () {
        processPdp();
      });
    }

    // Section rendering refresh — when Dawn swaps a section (e.g. cart
    // drawer re-render, theme editor block change), re-scan cards so any
    // freshly-rendered $0 prices get the badge treatment.
    document.addEventListener('shopify:section:load', function () {
      processAllCards();
    });

    // Collection & search grids re-render via AJAX on filter/sort/page
    // changes: FacetFiltersForm.renderProductGridContainer swaps
    // #ProductGridContainer's innerHTML without publishing any event, so
    // watch the grid and re-scan the freshly rendered cards. (Style edits
    // we make are attribute mutations; only childList is observed, so
    // hiding prices can't re-trigger the observer.)
    var grid = document.getElementById('ProductGridContainer');
    if (grid && 'MutationObserver' in window) {
      var rescan = null;
      new MutationObserver(function () {
        if (rescan) return;
        rescan = window.requestAnimationFrame(function () {
          rescan = null;
          processAllCards();
        });
      }).observe(grid, { childList: true, subtree: true });
    }

    // Predictive search results inject into the predictive-search dropdowns
    // (the desktop header search bar + the search modal) on each keystroke, with
    // no section:load event. Hide $0 prices the same as cards. Process
    // SYNCHRONOUSLY in the mutation callback: an rAF-debounced pass runs at a
    // moment Dawn overwrites, so the hide doesn't stick; setting display (an
    // attribute change) isn't observed here, so there's no re-trigger loop.
    if ('MutationObserver' in window) {
      document.querySelectorAll('predictive-search').forEach(function (el) {
        new MutationObserver(function () {
          processPredictive(el);
        }).observe(el, { childList: true, subtree: true });
      });
    }
  });

  /* ---------- Cards (collection grid / featured / related) ---------- */

  function processAllCards() {
    document.querySelectorAll('.card-wrapper, .grid__item .card').forEach(processCard);
  }

  function processCard(scope) {
    var priceEls = scope.querySelectorAll('.price');
    for (var i = 0; i < priceEls.length; i++) {
      hideIfZeroPrice(priceEls[i]);
    }
  }

  /* ---------- Predictive search dropdown ---------- */

  // Same as cards: a $0 search result shows nothing where the price would be.
  function processPredictive(scope) {
    var priceEls = scope.querySelectorAll('.price');
    for (var i = 0; i < priceEls.length; i++) {
      hideIfZeroPrice(priceEls[i]);
    }
  }

  // On cards a quote-only product just shows nothing where the price
  // would be — no "Quote on request" badge. The card's link, title, and
  // P/N still carry it through to the PDP, where the badge does appear.
  function hideIfZeroPrice(priceEl) {
    if (!priceEl) return;
    if (isZeroPrice(priceEl)) {
      priceEl.style.display = 'none';
    }
  }

  /* ---------- PDP ---------- */

  function processPdp() {
    var section = document.querySelector('product-info');
    if (!section) return;
    // Dawn names the PDP price element `id="price-{sectionId}"` (lowercase).
    var priceEl = section.querySelector('[id^="price-"]');
    if (!priceEl) return;
    // Read before badging: once the badge is in, the element has no digits.
    var zero = isZeroPrice(priceEl) || !!priceEl.querySelector('.custom-quote-badge');
    maybeBadge(priceEl);
    syncSubmitLabel(section, zero);
  }

  // The buy button says what happens next: a quote-only part is added to a
  // quote list, not bought. Liquid sets the first render (buy-buttons.liquid);
  // Dawn's product-form.js writes "Add to cart" back on every variant change,
  // and variantChange fires after that, so this runs last.
  function syncSubmitLabel(section, zero) {
    var btn = section.querySelector('.product-form__submit');
    if (!btn || btn.disabled) return; // "Sold out" / "Unavailable" stay as they are
    var span = btn.querySelector('span');
    var strings = window.variantStrings || {};
    var want = zero ? (strings.addToQuote || 'Add to quote') : (strings.addToCart || 'Add to cart');
    if (span && span.textContent.trim() !== want) span.textContent = want;
  }

  /* ---------- Shared price evaluator ---------- */

  function maybeBadge(priceEl) {
    if (!priceEl) return;
    if (isZeroPrice(priceEl)) {
      if (priceEl.querySelector('.custom-quote-badge')) return;
      priceEl.innerHTML =
        '<span class="custom-quote-badge custom-quote-badge--large">' +
        QUOTE_TEXT +
        '</span>';
    }
    // If the price is non-zero (e.g. variant change brought a priced
    // variant), Dawn's swap has already restored the correct money text.
    // Nothing to undo.
  }

  function isZeroPrice(priceEl) {
    // Walk visible `.price-item` elements rendered by Dawn's `price.liquid`.
    // We skip:
    //   - `.visually-hidden` labels ("Regular price", "Sale price", …)
    //   - strikethrough compare-at prices (`<s>` ancestor) — those are the
    //     "was" price on a sale, not the current charge.
    // The product is quote-only when every remaining numeric token reads 0.
    var items = priceEl.querySelectorAll('.price-item:not(.visually-hidden)');
    if (!items.length) return false;
    var sawNumber = false;
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      if (item.tagName === 'S' || (item.closest && item.closest('s'))) continue;
      var n = parseMoney(item.textContent);
      if (!isNaN(n)) {
        sawNumber = true;
        if (n !== 0) return false;
      }
    }
    return sawNumber;
  }

  // Parses the first money-shaped token out of a free-text string.
  // Handles "$0.00", "0,00 €", "USD 1,234.56", "From $0.00", etc. — both
  // dot-decimal and comma-decimal locales.
  function parseMoney(text) {
    if (!text) return NaN;
    var match = String(text).match(/-?\d[\d.,]*/);
    if (!match) return NaN;
    var raw = match[0];
    var lastSep = Math.max(raw.lastIndexOf('.'), raw.lastIndexOf(','));
    // If the last separator is followed by exactly 1–2 digits it's the
    // decimal mark; everything else is a thousands separator.
    if (lastSep > -1 && raw.length - lastSep - 1 <= 2 && raw.length - lastSep - 1 >= 1) {
      var intPart = raw.slice(0, lastSep).replace(/[.,]/g, '');
      var decPart = raw.slice(lastSep + 1);
      raw = intPart + '.' + decPart;
    } else {
      raw = raw.replace(/[.,]/g, '');
    }
    return parseFloat(raw);
  }
})();
